import { useState } from "react";
import { Mail, ArrowRight, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { externalSupabase } from "@/lib/externalSupabase";
import type { BookingDetails } from "@/components/BookingDetailsForm";

interface EmailCaptureModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (email: string) => void;
  listingUrl: string;
  manualBooking?: BookingDetails;
}

const EmailCaptureModal = ({ open, onClose, onSuccess, listingUrl, manualBooking }: EmailCaptureModalProps) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();

    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      // 1. Upsert guest email
      const { data: guestData, error: guestError } = await externalSupabase
        .from("guests")
        .upsert({ email: trimmed } as any, { onConflict: "email" })
        .select("id")
        .single();

      if (guestError) {
        console.error("Guest upsert error:", guestError);
        throw guestError;
      }

      console.log("Guest saved, id:", guestData?.id);

      // 2. Parse booking params from URL or use manual input
      const parsedParams: Record<string, any> = {};
      try {
        const parsed = new URL(listingUrl);
        const checkIn = parsed.searchParams.get("check_in");
        const checkOut = parsed.searchParams.get("check_out");
        const adults = parsed.searchParams.get("adults");
        const children = parsed.searchParams.get("children");
        if (checkIn) parsedParams.check_in = checkIn;
        if (checkOut) parsedParams.check_out = checkOut;
        if (adults) parsedParams.adults = parseInt(adults, 10);
        if (children) parsedParams.children = parseInt(children, 10);
      } catch {
        console.warn("Could not parse URL params from listing URL");
      }

      // Override with manual booking details if URL params were missing
      if (manualBooking) {
        if (manualBooking.checkIn && !parsedParams.check_in) parsedParams.check_in = manualBooking.checkIn;
        if (manualBooking.checkOut && !parsedParams.check_out) parsedParams.check_out = manualBooking.checkOut;
        if (!parsedParams.adults) parsedParams.adults = manualBooking.adults;
        if (!parsedParams.children && manualBooking.children > 0) parsedParams.children = manualBooking.children;
      }

      // 3. Insert search request with guest_email, airbnb_url, and parsed params
      const { data: searchData, error: searchError } = await externalSupabase
        .from("search_requests")
        .insert({
          guest_email: trimmed,
          airbnb_url: listingUrl,
          ...parsedParams,
        } as any)
        .select();

      if (searchError) {
        console.error("Search error:", searchError);
        throw searchError;
      }

      console.log("Search saved:", searchData);

      onSuccess(trimmed);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center items-center">
          <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center mx-auto mb-2">
            <Search className="w-5 h-5 text-accent-foreground" />
          </div>
          <DialogTitle className="font-display text-xl">
            Enter your email to search
          </DialogTitle>
          <DialogDescription>
            Get free access to our listing lookup tool. No spam, ever.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              autoFocus
              className="w-full pl-10 pr-4 py-3 rounded-xl border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow"
            />
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-hero text-primary-foreground py-3 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity shadow-savings disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? "Submitting..." : (
              <>
                Continue to Search
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EmailCaptureModal;
